import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  allocateTenantSmsNumber,
  assignTenantSmsNumber,
  getAuditLog,
  getTenant,
  getTenantHealth,
  getTenantPromptModule,
  getTenantPromptModuleAllowlist,
  getTenantSmsNumber,
  impersonateTenantAppUser,
  inviteTenantAppUser,
  listPromptModules,
  listSmsNumbers,
  listTenantAppUsers,
  putTenantAiDisclosure,
  putTenantBranding,
  putTenantPromptModule,
  putTenantPromptModuleAllowlist,
  releaseTenantSmsNumber,
  rentTenantSmsNumber,
  removeTenantAppUser,
  resendTenantAppUserInvite,
  setTenantSmsCountry,
  type AuditLogRow,
  type PromptModule,
  type SmsNumber,
  type TenantAppUser,
  type TenantHealthRow,
  type TenantPromptModuleAllowlist,
  type TenantPromptModuleAssignment,
  type TenantSmsState,
  type TenantSummary,
} from "@/lib/api";
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  Modal,
  PageContainer,
  Select,
  TextArea,
  TextInput,
  toErrorMessage,
  useConfirm,
} from "@/components/Forms";
import { ArrowLeftIcon } from "@/components/Icons";
import { verticalLabel } from "@/lib/verticals";

export default function TenantDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [tenant, setTenant] = useState<TenantSummary | null>(null);
  const [health, setHealth] = useState<TenantHealthRow | null>(null);
  const [audit, setAudit] = useState<AuditLogRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    Promise.all([
      getTenant(slug),
      getTenantHealth().catch(() => ({ workerId: null, tenants: [] })),
      getAuditLog({ tenant: slug, limit: 20 }).catch(() => ({ limit: 20, offset: 0, count: 0, rows: [] })),
    ])
      .then(([tn, h, a]) => {
        if (cancelled) return;
        setTenant(tn);
        setHealth(h.tenants.find((row) => row.slug === slug) ?? null);
        setAudit(a.rows);
      })
      .catch((e) => !cancelled && setError(toErrorMessage(e, t("tenantDetail.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [slug, t]);

  if (error) {
    return (
      <PageContainer title={t("tenantDetail.title")} subtitle={t("dashboard.subtitle")}>
        <ErrorBanner message={error} />
        <Link to="/tenants" className="text-sm text-accent hover:underline">
          {t("tenantDetail.backToTenants")}
        </Link>
      </PageContainer>
    );
  }
  if (!tenant) {
    return (
      <PageContainer title={t("tenantDetail.title")} subtitle={t("dashboard.subtitle")}>
        <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={tenant.name}
      subtitle={`${t("tenantDetail.title")} · ${tenant.slug}`}
      actions={
        <Link to="/tenants" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeftIcon size={14} /> {t("nav.tenants")}
        </Link>
      }
    >
      <div className="grid lg:grid-cols-2 gap-3">
        <Card title={t("tenantDetail.title")}>
          <dl className="text-sm space-y-1.5">
            <Row label={t("tenantDetail.factSlug")}>
              <span className="font-mono">{tenant.slug}</span>
            </Row>
            <Row label={t("tenantDetail.factVerticals")}>
              <VerticalChips verticals={effectiveVerticals(tenant)} />
            </Row>
            <Row label={t("tenantDetail.factDistributor")}>{tenant.distributor ?? "—"}</Row>
            <Row label={t("tenantDetail.factStatus")}>
              <Badge tone={tenant.status === "ACTIVE" ? "success" : "warning"}>
                {tenant.status ?? "—"}
              </Badge>
            </Row>
            <Row label={t("tenantDetail.factDb")}>
              <span className="font-mono text-xs">{tenant.dbName ?? "—"}</span>
            </Row>
            <Row label="S3 bucket">
              <span className="font-mono text-xs">{tenant.s3Bucket ?? "—"}</span>
            </Row>
            <Row label={t("tenantDetail.factTimezone")}>{tenant.timezone ?? "—"}</Row>
            <Row label={t("tenantDetail.factLocale")}>{tenant.locale ?? "—"}</Row>
            <Row label={t("tenantDetail.factCreated")}>{tenant.createdAt ?? "—"}</Row>
          </dl>
        </Card>

        <Card title={t("nav.health")}>
          {health === null ? (
            <p className="text-sm text-muted-foreground">{t("health.empty")}</p>
          ) : (
            <dl className="text-sm space-y-1.5">
              <Row label={t("tenantDetail.factStatus")}>
                <Badge tone={health.status === "UP" ? "success" : "danger"}>{health.status}</Badge>
              </Row>
              <Row label={t("health.colDue")}>
                <span className="font-mono">{health.dueCount ?? 0}</span>
              </Row>
              <Row label={t("health.colLastTick")}>
                <span className="text-xs">{health.lastTickAt ?? "—"}</span>
              </Row>
              <Row label={t("health.colLastTickDuration")}>
                <span className="font-mono text-xs">{health.lastTickDurationMs ?? "—"} ms</span>
              </Row>
              {health.pool && (
                <>
                  <Row label={t("tenantDetail.poolTitle")}>
                    <span className="font-mono text-xs">{health.pool.name ?? "—"}</span>
                  </Row>
                  <Row label={t("tenantDetail.poolActive")}>
                    <span className="font-mono">
                      {health.pool.active ?? 0} / {health.pool.total ?? 0}
                    </span>
                  </Row>
                </>
              )}
            </dl>
          )}
        </Card>
      </div>

      <div className="h-3" />

      <BrandingCard tenant={tenant} onSaved={setTenant} />

      <div className="h-3" />

      <AiDisclosureCard tenant={tenant} onSaved={setTenant} />

      <div className="h-3" />

      {tenant.slug && <TenantSmsCard slug={tenant.slug} />}

      <div className="h-3" />

      {tenant.slug && <TenantUsersCard slug={tenant.slug} />}

      <div className="h-3" />

      {tenant.slug &&
        effectiveVerticals(tenant).map((vertical) => (
          <div key={`${tenant.slug}-${vertical}`}>
            <PromptModuleCard slug={tenant.slug} vertical={vertical} />
            <div className="h-3" />
            <PromptModuleAllowlistCard slug={tenant.slug} vertical={vertical} />
            <div className="h-3" />
          </div>
        ))}

      <Card title={t("tenantDetail.auditTitle")}>
        {audit === null ? (
          <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
        ) : audit.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("tenantDetail.auditEmpty")}</p>
        ) : (
          <ul className="divide-y divide-border">
            {audit.map((row) => (
              <li key={row.id} className="py-2 text-xs">
                <div className="flex justify-between gap-2 items-baseline">
                  <span className="font-mono">{row.action}</span>
                  <span className="text-muted-foreground">{row.createdAt}</span>
                </div>
                <div className="text-muted-foreground">
                  {row.actor ? `actor=${row.actor}` : ""}
                  {row.targetId ? ` · target=${row.targetType}:${row.targetId}` : ""}
                </div>
                {row.details != null && (
                  <pre className="mt-1 whitespace-pre-wrap break-words">
                    {typeof row.details === "string"
                      ? row.details
                      : JSON.stringify(row.details, null, 2)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </PageContainer>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right text-foreground">{children}</dd>
    </div>
  );
}

/**
 * Phase 18 / Slice C — defensive accessor for `tenant.verticals`. Pre-V7
 * tenants in a rolling deploy may surface as `verticals: null` from the
 * backend; fall back to the single `vertical` so the UI doesn't blank
 * out during the migration window.
 */
function effectiveVerticals(tenant: TenantSummary): string[] {
  if (tenant.verticals && tenant.verticals.length > 0) return tenant.verticals;
  if (tenant.vertical) return [tenant.vertical];
  return [];
}

function VerticalChips({ verticals }: { verticals: string[] }) {
  const { t } = useTranslation();
  if (verticals.length === 0) {
    return <span>—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1 justify-end">
      {verticals.map((v, i) => (
        <span
          key={v}
          className="inline-flex items-center gap-1 rounded-full bg-muted/40 px-2 py-0.5 text-[11px]"
        >
          {verticalLabel(v, t)}
          {i === 0 && verticals.length > 1 && (
            <Badge tone="success">{t("tenants.verticalPrimaryBadge")}</Badge>
          )}
        </span>
      ))}
    </div>
  );
}

function PromptModuleCard({ slug, vertical }: { slug: string; vertical: string }) {
  const { t } = useTranslation();
  const [assignment, setAssignment] = useState<TenantPromptModuleAssignment | null>(null);
  const [modules, setModules] = useState<PromptModule[]>([]);
  const [picked, setPicked] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getTenantPromptModule(slug), listPromptModules(vertical)])
      .then(([a, m]) => {
        if (cancelled) return;
        setAssignment(a);
        setModules(m.modules);
        setPicked(a.promptModuleId ?? "");
      })
      .catch((e) => !cancelled && setErr(toErrorMessage(e, t("tenantDetailPromptModule.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [slug, vertical, t]);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const updated = await putTenantPromptModule(slug, picked || null);
      setAssignment(updated);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const cardTitle = `${t("tenantDetailPromptModule.title")} · ${verticalLabel(vertical, t)}`;
  return (
    <Card title={cardTitle}>
      <ErrorBanner message={err} />
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[16rem]">
          <p className="text-xs text-muted-foreground mb-1">
            {t("tenantDetailPromptModule.currentAssignment")}{" "}
            {assignment?.promptModuleId ? (
              <Link
                to={`/prompt-modules/${assignment.promptModuleId}`}
                className="text-accent hover:underline"
              >
                {assignment.moduleName ?? assignment.promptModuleId}
              </Link>
            ) : (
              <span>{t("tenantDetailPromptModule.noneAssigned")}</span>
            )}
          </p>
          <Select value={picked} onChange={(e) => setPicked(e.target.value)}>
            <option value="">{t("tenantDetailPromptModule.placeholderSelect")}</option>
            {modules.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} · {m.status}
              </option>
            ))}
          </Select>
        </div>
        <Button variant="primary" onClick={save} disabled={busy}>
          {busy ? t("tenantDetailPromptModule.saving") : t("tenantDetailPromptModule.save")}
        </Button>
        {savedAt && (
          <span className="text-[11px] text-muted-foreground">
            {t("tenantDetailPromptModule.saved")} · {savedAt}
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * Phase 17 — super-admin gate over which catalog modules a tenant may
 * assign to its lead sources. Empty selection = unrestricted (every
 * PUBLISHED module in the tenant's vertical is assignable, the V4
 * default). Non-empty = only the listed modules are visible to the
 * tenant operator's lead-source editor.
 */
function PromptModuleAllowlistCard({
  slug,
  vertical,
}: {
  slug: string;
  vertical: string;
}) {
  const { t } = useTranslation();
  const [allowlist, setAllowlist] = useState<TenantPromptModuleAllowlist | null>(null);
  const [modules, setModules] = useState<PromptModule[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getTenantPromptModuleAllowlist(slug), listPromptModules(vertical)])
      .then(([a, m]) => {
        if (cancelled) return;
        setAllowlist(a);
        setModules(m.modules);
        setPicked(new Set(a.moduleIds));
      })
      .catch(
        (e) => !cancelled && setErr(toErrorMessage(e, t("tenantDetailAllowlist.loadError"))),
      );
    return () => {
      cancelled = true;
    };
  }, [slug, vertical, t]);

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const updated = await putTenantPromptModuleAllowlist(slug, Array.from(picked));
      setAllowlist(updated);
      setPicked(new Set(updated.moduleIds));
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setPicked(new Set(allowlist?.moduleIds ?? []));
  }

  function clearAll() {
    setPicked(new Set());
  }

  const dirty =
    allowlist != null &&
    (picked.size !== allowlist.moduleIds.length ||
      Array.from(picked).some((id) => !allowlist.moduleIds.includes(id)));

  const cardTitle = `${t("tenantDetailAllowlist.title")} · ${verticalLabel(vertical, t)}`;
  return (
    <Card title={cardTitle}>
      <ErrorBanner message={err} />
      <p className="text-xs text-muted-foreground mb-2">
        {allowlist?.allowlistActive
          ? t("tenantDetailAllowlist.activeExplainer", {
              count: allowlist.moduleIds.length,
            })
          : t("tenantDetailAllowlist.unrestrictedExplainer")}
      </p>

      {modules.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {t("tenantDetailAllowlist.noModules", { vertical })}
        </p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
          {modules.map((m) => (
            <label
              key={m.id}
              className="flex items-start gap-2 text-xs cursor-pointer hover:bg-muted/30 rounded px-1 py-0.5"
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={picked.has(m.id)}
                onChange={() => toggle(m.id)}
              />
              <span className="flex-1">
                <span className="font-medium">{m.name}</span>
                <Badge tone={m.status === "PUBLISHED" ? "success" : "neutral"}>{m.status}</Badge>
                {m.description && (
                  <span className="block text-[11px] text-muted-foreground">
                    {m.description}
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button variant="primary" onClick={save} disabled={busy || !dirty}>
          {busy ? t("tenantDetailAllowlist.saving") : t("tenantDetailAllowlist.save")}
        </Button>
        <Button variant="ghost" onClick={reset} disabled={busy || !dirty}>
          {t("tenantDetailAllowlist.reset")}
        </Button>
        <Button variant="ghost" onClick={clearAll} disabled={busy || picked.size === 0}>
          {t("tenantDetailAllowlist.clearAll")}
        </Button>
        {savedAt && (
          <span className="text-[11px] text-muted-foreground">
            {t("tenantDetailAllowlist.saved")} · {savedAt}
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * Phase 47 — customer-org branding the super-admin can edit post-creation.
 * Mirrors the Create-tenant modal's Brand / From email / SMS sender id fields
 * (tenant.brand_name / from_email / sender_id_sms): the customer's own brand
 * interpolated into outbound copy ($brand), NOT the product brand
 * (heidrun/hejson), which is a per-deployment identity. The tenant can also
 * self-edit these from its own Settings page (tenant_admin only).
 */
function BrandingCard({
  tenant,
  onSaved,
}: {
  tenant: TenantSummary;
  onSaved: (next: TenantSummary) => void;
}) {
  const { t } = useTranslation();
  const initialBrand = tenant.brandName ?? "";
  const initialEmail = tenant.fromEmail ?? "";
  const initialSender = tenant.senderIdSms ?? "";
  const [brandName, setBrandName] = useState(initialBrand);
  const [fromEmail, setFromEmail] = useState(initialEmail);
  const [senderIdSms, setSenderIdSms] = useState(initialSender);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const dirty =
    brandName !== initialBrand ||
    fromEmail !== initialEmail ||
    senderIdSms !== initialSender;

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const next = await putTenantBranding(tenant.slug, {
        brandName: brandName.trim() === "" ? null : brandName,
        fromEmail: fromEmail.trim() === "" ? null : fromEmail,
        senderIdSms: senderIdSms.trim() === "" ? null : senderIdSms,
      });
      onSaved(next);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setErr(toErrorMessage(e, t("tenantDetailBranding.saveError")));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setBrandName(initialBrand);
    setFromEmail(initialEmail);
    setSenderIdSms(initialSender);
  }

  return (
    <Card title={t("tenantDetailBranding.title")}>
      <ErrorBanner message={err} />
      <p className="text-xs text-muted-foreground mb-2">
        {t("tenantDetailBranding.explainer")}
      </p>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label={t("tenantDetailBranding.brandNameLabel")}>
          <TextInput
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            placeholder={t("tenantDetailBranding.brandNamePlaceholder")}
          />
        </Field>
        <Field
          label={t("tenantDetailBranding.fromEmailLabel")}
          hint={t("tenantDetailBranding.fromEmailHint")}
        >
          <TextInput
            type="email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            placeholder="no-reply@example.com"
          />
        </Field>
        <Field label={t("tenantDetailBranding.senderIdSmsLabel")}>
          <TextInput
            value={senderIdSms}
            onChange={(e) => setSenderIdSms(e.target.value)}
            placeholder="+46710000000"
          />
        </Field>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button variant="primary" onClick={save} disabled={busy || !dirty}>
          {busy ? t("tenantDetailBranding.saving") : t("tenantDetailBranding.save")}
        </Button>
        <Button variant="ghost" onClick={reset} disabled={busy || !dirty}>
          {t("tenantDetailBranding.reset")}
        </Button>
        {savedAt && (
          <span className="text-[11px] text-muted-foreground">
            {t("tenantDetailBranding.saved")} · {savedAt}
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * Per-tenant override for the AI-identity disclosure appended to the first
 * LLM-generated outbound. Blank fields fall back to the shared template at
 * verticals/_shared/disclosure_ai.{locale}.vm (resolved in the engine, not
 * here). We keep Swedish + English fields independently editable because
 * the engine picks by lead locale, not tenant locale.
 */
function AiDisclosureCard({
  tenant,
  onSaved,
}: {
  tenant: TenantSummary;
  onSaved: (next: TenantSummary) => void;
}) {
  const { t } = useTranslation();
  const initialSv = tenant.aiDisclosureSv ?? "";
  const initialEn = tenant.aiDisclosureEn ?? "";
  const [sv, setSv] = useState(initialSv);
  const [en, setEn] = useState(initialEn);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const dirty = sv !== initialSv || en !== initialEn;

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const next = await putTenantAiDisclosure(
        tenant.slug,
        sv.trim() === "" ? null : sv,
        en.trim() === "" ? null : en,
      );
      onSaved(next);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setErr(toErrorMessage(e, t("tenantDetailAiDisclosure.loadError")));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setSv(initialSv);
    setEn(initialEn);
  }

  return (
    <Card title={t("tenantDetailAiDisclosure.title")}>
      <ErrorBanner message={err} />
      <p className="text-xs text-muted-foreground mb-2">
        {t("tenantDetailAiDisclosure.explainer")}
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">
            {t("tenantDetailAiDisclosure.svLabel")}
          </label>
          <TextArea
            rows={3}
            value={sv}
            onChange={(e) => setSv(e.target.value)}
            placeholder={t("tenantDetailAiDisclosure.svPlaceholder")}
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">
            {t("tenantDetailAiDisclosure.enLabel")}
          </label>
          <TextArea
            rows={3}
            value={en}
            onChange={(e) => setEn(e.target.value)}
            placeholder={t("tenantDetailAiDisclosure.enPlaceholder")}
          />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button variant="primary" onClick={save} disabled={busy || !dirty}>
          {busy ? t("tenantDetailAiDisclosure.saving") : t("tenantDetailAiDisclosure.save")}
        </Button>
        <Button variant="ghost" onClick={reset} disabled={busy || !dirty}>
          {t("tenantDetailAiDisclosure.reset")}
        </Button>
        {savedAt && (
          <span className="text-[11px] text-muted-foreground">
            {t("tenantDetailAiDisclosure.saved")} · {savedAt}
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * Phase 42 — platform-owned SMS number allocation. Replaces the tenant BYO
 * credentials surface: the super-admin allocates a from-number to the tenant
 * from the pool (or assigns a specific one), and the engine sends from it via
 * the platform's own provider account. Releasing returns it to the pool.
 */
const SMS_PROVIDERS = ["46elks", "twilio"];

/**
 * Operating-country choices (Phase 53). Nordics route via 46elks, the rest via
 * Twilio; the empty value clears the override and auto-detects from the sender
 * number / locale. Curated to the markets we actively support.
 */
const SMS_COUNTRIES: { code: string; name: string }[] = [
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "DK", name: "Denmark" },
  { code: "FI", name: "Finland" },
  { code: "DE", name: "Germany" },
  { code: "US", name: "United States" },
];

function TenantSmsCard({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [state, setState] = useState<TenantSmsState | null>(null);
  const [pool, setPool] = useState<SmsNumber[]>([]);
  const [provider, setProvider] = useState(SMS_PROVIDERS[0]);
  const [pickedFree, setPickedFree] = useState("");
  const [manualE164, setManualE164] = useState("");
  const [manualLabel, setManualLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [reloadAt, setReloadAt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getTenantSmsNumber(slug), listSmsNumbers()])
      .then(([s, p]) => {
        if (cancelled) return;
        setState(s);
        setPool(p.numbers);
      })
      .catch((e) => !cancelled && setErr(toErrorMessage(e, t("tenantSms.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [slug, reloadAt, t]);

  const freeForProvider = pool.filter((n) => n.status === "FREE" && n.provider === provider);
  const active = state?.numbers?.[0] ?? null;

  async function run(fn: () => Promise<TenantSmsState>) {
    setBusy(true);
    setErr(null);
    try {
      const next = await fn();
      setState(next);
      setPickedFree("");
      setManualE164("");
      setManualLabel("");
      setReloadAt((n) => n + 1);
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function release() {
    const ok = await confirm({
      title: t("tenantSms.releaseConfirmTitle"),
      body: t("tenantSms.releaseConfirmBody"),
      confirmLabel: t("tenantSms.release"),
      destructive: true,
    });
    if (!ok) return;
    await run(() => releaseTenantSmsNumber(slug));
  }

  async function allocate() {
    if (!pickedFree) return;
    await run(() => allocateTenantSmsNumber(slug, Number(pickedFree)));
  }

  async function changeCountry(code: string) {
    await run(() => setTenantSmsCountry(slug, code));
  }

  async function assign() {
    if (!manualE164.trim()) {
      setErr(t("tenantSms.errNumberRequired"));
      return;
    }
    await run(() =>
      assignTenantSmsNumber(slug, {
        provider,
        e164: manualE164.trim(),
        label: manualLabel.trim() || null,
      }),
    );
  }

  async function rent() {
    const ok = await confirm({
      title: t("tenantSms.rentConfirmTitle"),
      body: t("tenantSms.rentConfirmBody"),
      confirmLabel: t("tenantSms.rent"),
    });
    if (!ok) return;
    await run(() => rentTenantSmsNumber(slug, { provider }));
  }

  return (
    <Card title={t("tenantSms.title")}>
      <ErrorBanner message={err} />
      <p className="text-xs text-muted-foreground mb-3">{t("tenantSms.explainer")}</p>

      <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-border/60 bg-background/40 p-3">
        <div className="text-sm">
          {active ? (
            <>
              <div className="font-mono">{active.e164}</div>
              <div className="text-xs text-muted-foreground">{active.provider}</div>
            </>
          ) : (
            <span className="text-muted-foreground">{t("tenantSms.none")}</span>
          )}
        </div>
        {active && (
          <Button variant="destructive" onClick={release} disabled={busy}>
            {t("tenantSms.release")}
          </Button>
        )}
      </div>

      <Field label={t("tenantSms.countryLabel")}>
        <Select
          value={state?.country ?? ""}
          onChange={(e) => changeCountry(e.target.value)}
          disabled={busy}
        >
          <option value="">{t("tenantSms.countryAuto")}</option>
          {SMS_COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name} ({c.code})
            </option>
          ))}
        </Select>
      </Field>
      {state?.carrier && (
        <p className="mt-1 text-xs text-muted-foreground">
          {t("tenantSms.countryRoutes", {
            carrier: state.carrier,
            country: state.resolvedCountry ?? "",
          })}
        </p>
      )}

      <div className="mt-3">
        <Field label={t("tenantSms.provider")}>
        <Select
          value={provider}
          onChange={(e) => {
            setProvider(e.target.value);
            setPickedFree("");
          }}
        >
          {SMS_PROVIDERS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </Select>
        </Field>
      </div>

      <div className="mt-3">
        <label className="block text-xs text-muted-foreground mb-1">
          {t("tenantSms.allocateLabel")}
        </label>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[14rem]">
            <Select value={pickedFree} onChange={(e) => setPickedFree(e.target.value)}>
              <option value="">
                {freeForProvider.length ? t("tenantSms.pickFree") : t("tenantSms.noFree")}
              </option>
              {freeForProvider.map((n) => (
                <option key={n.id} value={String(n.id)}>
                  {n.e164}
                  {n.label ? ` · ${n.label}` : ""}
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant="primary"
            onClick={() => allocate()}
            disabled={busy || !pickedFree}
          >
            {t("tenantSms.allocate")}
          </Button>
        </div>
      </div>

      {provider === "46elks" && (
        <div className="mt-4">
          <label className="block text-xs text-muted-foreground mb-1">
            {t("tenantSms.rentLabel")}
          </label>
          <Button variant="primary" onClick={() => rent()} disabled={busy}>
            {busy ? t("tenantSms.renting") : t("tenantSms.rent")}
          </Button>
        </div>
      )}

      <div className="mt-4">
        <label className="block text-xs text-muted-foreground mb-1">
          {t("tenantSms.assignLabel")}
        </label>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[10rem]">
            <TextInput
              value={manualE164}
              onChange={(e) => setManualE164(e.target.value)}
              placeholder="+46766001122"
            />
          </div>
          <div className="flex-1 min-w-[8rem]">
            <TextInput
              value={manualLabel}
              onChange={(e) => setManualLabel(e.target.value)}
              placeholder={t("tenantSms.labelPlaceholder")}
            />
          </div>
          <Button variant="ghost" onClick={() => assign()} disabled={busy || !manualE164.trim()}>
            {t("tenantSms.assign")}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Phase 35 Slice C — super-admin provisions tenant operator logins from the
 * /admin console. List + invite (admin / operator). The invitee gets an email
 * with a set-password link; sales-rep provisioning stays on the tenant's own
 * /users page (needs a calendar identity).
 */
type TenantResendResult = { email: string; inviteSent: boolean; setPasswordUrl: string };

function TenantUsersCard({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [users, setUsers] = useState<TenantAppUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [reloadAt, setReloadAt] = useState(0);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);
  const [resendResult, setResendResult] = useState<TenantResendResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    listTenantAppUsers(slug)
      .then((r) => !cancelled && setUsers(r.users))
      .catch((e) => !cancelled && setErr(toErrorMessage(e, t("tenantUsers.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [slug, reloadAt, t]);

  async function resend(u: TenantAppUser) {
    setErr(null);
    setResendingId(u.id);
    try {
      const r = await resendTenantAppUserInvite(slug, u.id);
      setResendResult({ email: u.email, inviteSent: r.inviteSent, setPasswordUrl: r.setPasswordUrl });
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setResendingId(null);
    }
  }

  async function remove(u: TenantAppUser) {
    const ok = await confirm({
      title: t("tenantUsers.removeConfirmTitle"),
      body: t("tenantUsers.removeConfirmBody", { email: u.email }),
      confirmLabel: t("tenantUsers.remove"),
      destructive: true,
    });
    if (!ok) return;
    setErr(null);
    setRemovingId(u.id);
    try {
      await removeTenantAppUser(slug, u.id);
      setReloadAt((n) => n + 1);
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setRemovingId(null);
    }
  }

  async function signInAs(u: TenantAppUser) {
    setErr(null);
    setImpersonatingId(u.id);
    try {
      const r = await impersonateTenantAppUser(slug, u.id);
      // The server has swapped this session to the target tenant user; hard-
      // navigate into the operator SPA as them. Deliberately don't reset
      // impersonatingId — the page is leaving.
      window.location.href = r.redirect;
    } catch (e) {
      setErr(toErrorMessage(e));
      setImpersonatingId(null);
    }
  }

  return (
    <Card title={t("tenantUsers.title")}>
      <ErrorBanner message={err} />
      <p className="text-xs text-muted-foreground mb-2">{t("tenantUsers.explainer")}</p>
      <div className="mb-3">
        <Button variant="primary" onClick={() => setInviting(true)}>
          {t("tenantUsers.invite")}
        </Button>
      </div>
      {users === null ? (
        <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
      ) : users.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("tenantUsers.empty")}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left py-1">{t("tenantUsers.colUser")}</th>
              <th className="text-left py-1">{t("tenantUsers.colRole")}</th>
              <th className="text-left py-1">{t("tenantUsers.colStatus")}</th>
              <th className="text-right py-1">{t("tenantUsers.colActions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((u) => (
              <tr key={u.id}>
                <td className="py-1.5">
                  <div className="font-medium">{u.fullName ?? u.email}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </td>
                <td className="py-1.5">
                  <Badge tone={u.role === "tenant_admin" ? "info" : "neutral"}>
                    {u.role === "tenant_admin"
                      ? t("tenantUsers.roleAdmin")
                      : t("tenantUsers.roleOperator")}
                  </Badge>
                </td>
                <td className="py-1.5">
                  {!u.enabled ? (
                    <Badge tone="warning">{t("tenantUsers.statusDisabled")}</Badge>
                  ) : !u.hasPassword ? (
                    <Badge tone="info">{t("tenantUsers.statusInvited")}</Badge>
                  ) : (
                    <span className="text-xs text-emerald-400">{t("tenantUsers.statusActive")}</span>
                  )}
                </td>
                <td className="py-1.5">
                  <div className="flex items-center justify-end gap-1.5">
                    {u.enabled && (
                      <Button onClick={() => signInAs(u)} disabled={impersonatingId === u.id}>
                        {impersonatingId === u.id
                          ? t("tenantUsers.signingIn")
                          : t("tenantUsers.signInAs")}
                      </Button>
                    )}
                    {u.enabled && !u.hasPassword && (
                      <Button onClick={() => resend(u)} disabled={resendingId === u.id}>
                        {resendingId === u.id ? t("tenantUsers.resending") : t("tenantUsers.resend")}
                      </Button>
                    )}
                    {u.role !== "sales_rep" && (
                      <Button
                        variant="destructive"
                        onClick={() => remove(u)}
                        disabled={removingId === u.id}
                      >
                        {removingId === u.id ? t("tenantUsers.removing") : t("tenantUsers.remove")}
                      </Button>
                    )}
                    {!u.enabled && u.role === "sales_rep" && (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <InviteTenantUserModal
        open={inviting}
        slug={slug}
        onClose={() => setInviting(false)}
        onInvited={() => setReloadAt((n) => n + 1)}
      />
      <Modal
        open={resendResult !== null}
        title={t("tenantUsers.resendResultTitle")}
        onClose={() => setResendResult(null)}
        footer={
          <Button variant="primary" onClick={() => setResendResult(null)}>
            {t("forms.close")}
          </Button>
        }
      >
        {resendResult && (
          <div className="space-y-3">
            <p className="text-sm">
              {resendResult.inviteSent
                ? t("tenantUsers.resendSentTo", { email: resendResult.email })
                : t("tenantUsers.inviteNotEmailed")}
            </p>
            <Field label={t("tenantUsers.setPasswordLink")}>
              <TextInput
                readOnly
                value={resendResult.setPasswordUrl}
                onFocus={(e) => e.target.select()}
              />
            </Field>
          </div>
        )}
      </Modal>
    </Card>
  );
}

function InviteTenantUserModal({
  open,
  slug,
  onClose,
  onInvited,
}: {
  open: boolean;
  slug: string;
  onClose: () => void;
  onInvited: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("tenant_admin");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ inviteSent: boolean; setPasswordUrl: string; email: string } | null>(null);

  useEffect(() => {
    if (open) {
      setEmail("");
      setFullName("");
      setRole("tenant_admin");
      setErr(null);
      setResult(null);
    }
  }, [open]);

  async function submit() {
    if (!email.trim()) {
      setErr(t("tenantUsers.fieldEmail"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await inviteTenantAppUser(slug, {
        email: email.trim(),
        fullName: fullName.trim() || null,
        role,
      });
      setResult({ inviteSent: r.inviteSent, setPasswordUrl: r.setPasswordUrl, email: email.trim() });
      onInvited();
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={t("tenantUsers.modalTitle")}
      onClose={onClose}
      footer={
        result ? (
          <Button variant="primary" onClick={onClose}>
            {t("forms.close")}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              {t("forms.cancel")}
            </Button>
            <Button variant="primary" onClick={submit} disabled={busy}>
              {busy ? t("tenantUsers.inviteSending") : t("tenantUsers.inviteAction")}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-3">
          <p className="text-sm">
            {result.inviteSent
              ? t("tenantUsers.inviteSentTo", { email: result.email })
              : t("tenantUsers.inviteNotEmailed")}
          </p>
          <Field label={t("tenantUsers.setPasswordLink")}>
            <TextInput readOnly value={result.setPasswordUrl} onFocus={(e) => e.target.select()} />
          </Field>
        </div>
      ) : (
        <>
          <ErrorBanner message={err} />
          <div className="space-y-3">
            <Field label={t("tenantUsers.fieldEmail")} required>
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                placeholder="anna@example.com"
              />
            </Field>
            <Field label={t("tenantUsers.fieldName")}>
              <TextInput value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </Field>
            <Field label={t("tenantUsers.fieldRole")} required>
              <Select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="tenant_admin">{t("tenantUsers.roleAdmin")}</option>
                <option value="operator">{t("tenantUsers.roleOperator")}</option>
              </Select>
            </Field>
          </div>
        </>
      )}
    </Modal>
  );
}
